/**
 * Port API Service
 * Handles all API requests related to ports
 *
 * Pattern: object export (per CLAUDE.md, reference: taskApi.ts)
 */

import api from './api';
import {
  Port,
  PortContact,
  PortDocument,
  PortPayment,
  PortFilterOptions,
  PortNote,
  PortReminder,
  TerminalSiteOption,
} from '../types/port';

// ==================== Response Types ====================

interface PortListResponse {
  data: Port[];
  total: number;
  limit: number;
  offset: number;
}

type PortFilters = PortFilterOptions & {
  category?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
};

// ==================== API Object ====================

export const portApi = {
  // ==================== Port CRUD ====================

  /**
   * Get all ports with optional filters
   * GET /api/ports
   */
  async getAll(filters?: PortFilters): Promise<PortListResponse> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.portName) params.append('search', filters.portName);
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.offset) params.append('offset', filters.offset.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    }
    const response = await api.get(`/ports${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  },

  /**
   * Get port by ID
   * GET /api/ports/:id
   */
  async getById(id: string): Promise<Port> {
    const response = await api.get(`/ports/${id}`);
    return response.data;
  },

  /**
   * Lookup table of terminal_site_code values + labels (for modal dropdown).
   * GET /api/ports/terminal-sites
   */
  async getTerminalSites(): Promise<TerminalSiteOption[]> {
    const response = await api.get('/ports/terminal-sites');
    return response.data;
  },

  /**
   * Create a new port
   * POST /api/ports
   */
  async create(portData: Omit<Port, 'id' | 'createdDate' | 'lastUpdated'>): Promise<Port> {
    const response = await api.post('/ports', portData);
    return response.data;
  },

  /**
   * Update an existing port
   * PUT /api/ports/:id
   */
  async update(id: string, portData: Partial<Port>): Promise<Port> {
    const response = await api.put(`/ports/${id}`, portData);
    return response.data;
  },

  /**
   * Delete a port
   * DELETE /api/ports/:id
   */
  async delete(id: string): Promise<void> {
    const response = await api.delete(`/ports/${id}`);
    return response.data;
  },

  /**
   * Get port summary statistics
   * GET /api/ports/:id/summary
   */
  async getSummary(id: string): Promise<Record<string, unknown>> {
    const response = await api.get(`/ports/${id}/summary`);
    return response.data;
  },

  // ==================== Contacts ====================

  /**
   * Get all contacts for a port
   * GET /api/ports/:portId/contacts
   */
  async getContacts(portId: string): Promise<PortContact[]> {
    const response = await api.get(`/ports/${portId}/contacts`);
    return response.data;
  },

  /**
   * Create a new contact for a port
   * POST /api/ports/:portId/contacts
   */
  async createContact(portId: string, contactData: Omit<PortContact, 'id'>): Promise<PortContact> {
    const response = await api.post(`/ports/${portId}/contacts`, contactData);
    return response.data;
  },

  /**
   * Update a contact
   * PUT /api/ports/contacts/:contactId
   */
  async updateContact(contactId: string, contactData: Partial<PortContact>): Promise<PortContact> {
    const response = await api.put(`/ports/contacts/${contactId}`, contactData);
    return response.data;
  },

  /**
   * Delete a contact
   * DELETE /api/ports/contacts/:contactId
   */
  async deleteContact(contactId: string): Promise<void> {
    const response = await api.delete(`/ports/contacts/${contactId}`);
    return response.data;
  },

  // ==================== Documents ====================

  /**
   * Get all documents for a port
   * GET /api/ports/:portId/documents
   */
  async getDocuments(portId: string): Promise<PortDocument[]> {
    const response = await api.get(`/ports/${portId}/documents`);
    return response.data;
  },

  /**
   * Create a new document for a port
   * POST /api/ports/:portId/documents
   */
  async createDocument(portId: string, docData: Omit<PortDocument, 'id' | 'portId'>): Promise<PortDocument> {
    const response = await api.post(`/ports/${portId}/documents`, docData);
    return response.data;
  },

  /**
   * Delete a document
   * DELETE /api/ports/documents/:docId
   */
  async deleteDocument(docId: string): Promise<void> {
    const response = await api.delete(`/ports/documents/${docId}`);
    return response.data;
  },

  // ==================== Payments ====================

  /**
   * Get payment info for a port
   * GET /api/ports/:portId/payments
   */
  async getPayments(portId: string): Promise<PortPayment[]> {
    const response = await api.get(`/ports/${portId}/payments`);
    return response.data;
  },

  /**
   * Create or update payment info for a port
   * POST /api/ports/:portId/payments
   */
  async upsertPayment(portId: string, paymentData: Omit<PortPayment, 'id' | 'portId'>): Promise<PortPayment> {
    const response = await api.post(`/ports/${portId}/payments`, paymentData);
    return response.data;
  },

  // ==================== Notes ====================

  /**
   * Get all notes for a port
   * GET /api/ports/:portId/notes
   */
  async getNotes(portId: string): Promise<PortNote[]> {
    const response = await api.get(`/ports/${portId}/notes`);
    return response.data;
  },

  /**
   * Create a new note for a port
   * POST /api/ports/:portId/notes
   */
  async createNote(portId: string, noteData: Omit<PortNote, 'id'>): Promise<PortNote> {
    const response = await api.post(`/ports/${portId}/notes`, noteData);
    return response.data;
  },

  /**
   * Update a note
   * PUT /api/ports/notes/:noteId
   */
  async updateNote(noteId: string, noteData: Partial<PortNote>): Promise<PortNote> {
    const response = await api.put(`/ports/notes/${noteId}`, noteData);
    return response.data;
  },

  /**
   * Delete a note
   * DELETE /api/ports/notes/:noteId
   */
  async deleteNote(noteId: string): Promise<void> {
    const response = await api.delete(`/ports/notes/${noteId}`);
    return response.data;
  },

  // ==================== Reminders ====================

  /**
   * Get all reminders for a port
   * GET /api/ports/:portId/reminders
   */
  async getReminders(portId: string): Promise<PortReminder[]> {
    const response = await api.get(`/ports/${portId}/reminders`);
    return response.data;
  },

  /**
   * Create a new reminder for a port
   * POST /api/ports/:portId/reminders
   */
  async createReminder(portId: string, reminderData: Omit<PortReminder, 'id'>): Promise<PortReminder> {
    const response = await api.post(`/ports/${portId}/reminders`, reminderData);
    return response.data;
  },

  /**
   * Update a reminder
   * PUT /api/ports/reminders/:reminderId
   */
  async updateReminder(reminderId: string, reminderData: Partial<PortReminder>): Promise<PortReminder> {
    const response = await api.put(`/ports/reminders/${reminderId}`, reminderData);
    return response.data;
  },

  /**
   * Delete a reminder
   * DELETE /api/ports/reminders/:reminderId
   */
  async deleteReminder(reminderId: string): Promise<void> {
    const response = await api.delete(`/ports/reminders/${reminderId}`);
    return response.data;
  },
};

// ==================== Backward-Compatible Named Exports ====================
// These aliases maintain compatibility with existing `import { getAllPorts } from` consumers

export const getAllPorts = portApi.getAll;
export const getPortById = portApi.getById;
export const createPort = portApi.create;
export const updatePort = portApi.update;
export const deletePort = portApi.delete;
export const getPortSummary = portApi.getSummary;
export const getPortContacts = portApi.getContacts;
export const createPortContact = portApi.createContact;
export const updatePortContact = portApi.updateContact;
export const deletePortContact = portApi.deleteContact;
export const getPortDocuments = portApi.getDocuments;
export const createPortDocument = portApi.createDocument;
export const deletePortDocument = portApi.deleteDocument;
export const getPortPayments = portApi.getPayments;
export const upsertPortPayment = portApi.upsertPayment;
export const getPortNotes = portApi.getNotes;
export const createPortNote = portApi.createNote;
export const updatePortNote = portApi.updateNote;
export const deletePortNote = portApi.deleteNote;
export const getPortReminders = portApi.getReminders;
export const createPortReminder = portApi.createReminder;
export const updatePortReminder = portApi.updateReminder;
export const deletePortReminder = portApi.deleteReminder;

export default portApi;
